import React, { useState, useEffect } from 'react';
import './App.css';
import ChatWindow from './components/ChatWindow';
import RestaurantMarketplace from './components/RestaurantMarketplace';
import RestaurantMenu from './components/RestaurantMenu';
import ShoppingCart from './components/ShoppingCart';
import CheckoutFlow from './components/CheckoutFlow';
import UnifiedCheckout from './components/UnifiedCheckout';
import FestivalMap from './components/FestivalMap';
import CustomerHeader from './components/CustomerHeader';
import { startWebSocketService, stopWebSocketService } from './services/WebSocketService';
import { testWebSocketConnection } from './testWebSocket';

// NEW: Guest-first flow - no customer accounts needed
const guestInitialMessage = {
  text: "Hey there! Welcome to EZ Drink. I'm Zara! 🍹 I can see you have access to our connected vendors. Ready to discover amazing restaurants and place orders?",
  sender: "bot",
  buttons: [
    {
      text: "🍽️ Show Restaurant Marketplace",
      action: "show_restaurant_marketplace"
    },
    {
      text: "🗺️ Festival Map",
      action: "festival_map"
    },
    {
      text: "🛒 View Cart",
      action: "show_cart"
    },
    {
      text: "🍽️ Help Me Decide",
      action: "help_me_decide"
    },
    {
      text: "🔄 Refresh Vendor List",
      action: "refresh_vendors"
    }
  ]
};

// Main App Component - COMPLETELY GUTTED OLD CUSTOMER SYSTEM
const App = () => {
  // NEW STATE: Guest-first flow (no customer accounts)
  const [messages, setMessages] = useState([guestInitialMessage]);
  const [lastSuggestedDrink, setLastSuggestedDrink] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  
  // Customer marketplace state
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [showRestaurantMenu, setShowRestaurantMenu] = useState(false);
  const [showRestaurantMarketplace, setShowRestaurantMarketplace] = useState(false);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  
  // Global cart management functions
  const addToGlobalCart = (item, vendor) => {
    const cartItem = {
      ...item,
      id: `${vendor.id}-${item.id}-${Date.now()}`, // Unique ID including vendor
      vendorId: vendor.id,
      vendorName: vendor.name,
      vendorPosSystem: vendor.posSystem
    };
    
    setCart(prevCart => {
      const existingItem = prevCart.find(cartItem => 
        cartItem.id === item.id && cartItem.vendorId === vendor.id
      );
      
      if (existingItem) {
        return prevCart.map(cartItem =>
          cartItem.id === item.id && cartItem.vendorId === vendor.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      } else {
        return [...prevCart, { ...cartItem, quantity: 1 }];
      }
    });
  };

  const removeFromGlobalCart = (itemId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== itemId));
  };

  const updateGlobalCartQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromGlobalCart(itemId);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const getCartItemsForVendor = (vendorId) => {
    return cart.filter(item => item.vendorId === vendorId);
  };
  
  const [showCelebration, setShowCelebration] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  // Keep existing useEffect for logging
  useEffect(() => {
    console.log('State Update:', {
      lastSuggestedDrink,
      currentOrder,
      messageCount: messages.length,
      showCelebration,
      isProcessing,
      retryCount,
      selectedRestaurant,
      showRestaurantMarketplace,
      cartItemCount: cart.length
    });
  }, [messages, lastSuggestedDrink, currentOrder, showCelebration, isProcessing, retryCount, selectedRestaurant, showRestaurantMarketplace, cart]);

  // Initialize WebSocket service for real-time vendor notifications
  useEffect(() => {
    console.log('🚀 Initializing WebSocket service for vendor notifications...');
    startWebSocketService();
    
    // Cleanup on unmount
    return () => {
      console.log('🛑 Cleaning up WebSocket service...');
      stopWebSocketService();
    };
  }, []);

  // NEW: Simplified action handler for guest flow
  const handleGuestAction = (action, data) => {
    switch(action) {
      // Show restaurant marketplace
      case 'show_restaurant_marketplace':
        setShowRestaurantMarketplace(true);
        setMessages(prev => [...prev, {
          text: "🍽️ Here's our live restaurant marketplace! Discover amazing food trucks and vendors available now:",
          sender: "bot",
          showRestaurantMarketplace: true
        }]);
        break;

      // Festival Map integration
      case 'festival_map':
        console.log('Festival Map action triggered');
        break;

      // Help me decide integration  
      case 'help_me_decide':
        console.log('Help Me Decide action triggered');
        break;

      // Show restaurant menu
      case 'show_restaurant_menu':
        setShowRestaurantMenu(true);
        break;

      // Show shopping cart
      case 'show_cart':
        setShowCart(true);
        break;

      // NEW: Handle vendor selection from chat window
      case 'vendor_selected':
        console.log('Vendor selected from chat:', data);
        if (data && data.id) {
          // Show vendor details or menu
          setSelectedRestaurant({
            id: data.id,
            businessName: data.name,
            description: data.description,
            location: data.location,
            specialties: data.specialties || [],
            posSystem: data.posSystem
          });
          setShowRestaurantMenu(true);
          
          setMessages(prev => [...prev, {
            text: `Great choice! Here's ${data.name}'s information. They're connected via ${data.posSystem === 'eventbridge' ? 'Square' : data.posSystem}.`,
            sender: "bot",
            showRestaurantMenu: true
          }]);
        }
        break;

      // NEW: Handle vendor refresh request
      case 'refresh_vendors':
        console.log('Vendor refresh requested from chat');
        setMessages(prev => [...prev, {
          text: "🔄 Refreshing the vendor list to check for new connections...",
          sender: "bot"
        }]);
        // The ChatWindow component will handle the actual refresh
        break;

      // Add item to cart
      case 'add_to_cart':
        const newItem = { ...data, id: Date.now() };
        setCart(prev => [...prev, newItem]);
        setMessages(prev => [...prev, {
          text: `✅ Added ${data.name} to your cart!`,
          sender: "bot"
        }]);
        break;

      // Handle preference selections
      default:
        if (action.startsWith('preference_')) {
          const parts = action.split('_');
          if (parts.length >= 3) {
            const questionId = parts[1];
            const selectedOptionId = parts[2];
            handlePreferenceSelection(questionId, selectedOptionId);
          }
        }
        break;
    }
  };

  // Keep existing preference handling
  const handlePreferenceSelection = (questionId, selectedOptionId) => {
    console.log('Preference selected:', { questionId, selectedOptionId });
    // Handle preference logic here
  };

  // NEW: Handle restaurant selection from marketplace
  const handleRestaurantSelection = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setShowRestaurantMenu(true);
    setShowRestaurantMarketplace(false);
    
    setMessages(prev => [...prev, {
      text: `Great choice! Here's ${restaurant.businessName}'s menu:`,
      sender: "bot",
      showRestaurantMenu: true
    }]);
  };

  // NEW: Handle order placement (guest flow)
  const handleGuestOrder = (orderData) => {
    console.log('Guest order placed:', orderData);
    setCurrentOrder(orderData);
    setShowRestaurantMenu(false);
    
    setMessages(prev => [...prev, {
      text: "🎉 Order received! You'll get a confirmation shortly. No account needed - just enjoy your drinks!",
      sender: "bot"
    }]);
  };

  // NEW: Close restaurant menu and return to marketplace
  const handleCloseRestaurantMenu = () => {
    setShowRestaurantMenu(false);
    setSelectedRestaurant(null);
    setShowRestaurantMarketplace(true);
  };

  // NEW: Close restaurant marketplace
  const handleCloseRestaurantMarketplace = () => {
    setShowRestaurantMarketplace(false);
  };

  // Keep existing message handling
  const handleSendMessage = async (message, type = 'user') => {
    console.log('Message sent:', { message, type });
    
    // Add user message to chat
    setMessages(prevMessages => [...prevMessages, { 
      text: message, 
      sender: 'user' 
    }]);

    // Process message and generate response
    // This would integrate with your AI service
    const botResponse = {
      text: "Thanks for your message! I'm here to help you discover vendors and place orders.",
      sender: "bot"
    };

    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  return (
    <div className="App">
      {/* Customer Header */}
      <CustomerHeader 
        cart={cart}
        onShowCart={() => setShowCart(true)}
        onShowMarketplace={() => handleGuestAction('show_restaurant_marketplace')}
      />
      
      {/* Keep existing background styling */}
      <div className="app-background">
        <div className="gradient-overlay"></div>
        <div className="grid-pattern"></div>
      </div>

      <div className="relative" style={{ paddingTop: '80px' }}>
        {/* WebSocket Test Button */}
        <div className="fixed top-4 right-4 z-50">
          <button
            onClick={testWebSocketConnection}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold shadow-lg"
          >
            🧪 Test WebSocket
          </button>
        </div>

        {/* Main Chat Interface */}
        <ChatWindow
          messages={messages}
          onSendMessage={handleSendMessage}
          onButtonClick={handleGuestAction}
          inputType="text"
          currentUser={null}
          isProcessing={isProcessing}
          globalCart={cart}
          onAddToGlobalCart={addToGlobalCart}
          onRemoveFromGlobalCart={removeFromGlobalCart}
          onUpdateGlobalCartQuantity={updateGlobalCartQuantity}
          getCartItemsForVendor={getCartItemsForVendor}
        />

        {/* Restaurant Marketplace Overlay */}
        {showRestaurantMarketplace && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-gray-900 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-white">🍽️ Live Restaurant Marketplace</h2>
                <button
                  onClick={handleCloseRestaurantMarketplace}
                  className="text-gray-400 hover:text-white text-xl"
                >
                  ✕
                </button>
              </div>
              <RestaurantMarketplace
                onRestaurantSelect={handleRestaurantSelection}
                onClose={handleCloseRestaurantMarketplace}
              />
            </div>
          </div>
        )}

        {/* Restaurant Menu Overlay */}
        {showRestaurantMenu && selectedRestaurant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-gray-900 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-white">{selectedRestaurant.businessName} - Menu</h2>
                <button
                  onClick={handleCloseRestaurantMenu}
                  className="text-gray-400 hover:text-white text-xl"
                >
                  ✕
                </button>
              </div>
              <RestaurantMenu
                restaurant={selectedRestaurant}
                onOrder={handleGuestOrder}
                onClose={handleCloseRestaurantMenu}
                onAddToCart={(item) => handleGuestAction('add_to_cart', item)}
              />
            </div>
          </div>
        )}

        {/* Shopping Cart Overlay */}
        {showCart && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-gray-900 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <ShoppingCart
                items={cart}
                onClose={() => setShowCart(false)}
                onCheckout={() => {
                  setShowCart(false);
                  setShowCheckout(true);
                }}
                onRemoveItem={(itemId) => setCart(prev => prev.filter(item => item.id !== itemId))}
                onUpdateQuantity={(itemId, quantity) => {
                  setCart(prev => prev.map(item => 
                    item.id === itemId ? { ...item, quantity } : item
                  ));
                }}
              />
            </div>
          </div>
        )}

        {/* Unified Checkout Flow Overlay */}
        {showCheckout && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-gray-900 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <UnifiedCheckout
                cart={cart}
                onClose={() => setShowCheckout(false)}
                onSuccess={(orderData) => {
                  setShowCheckout(false);
                  setCurrentOrder(orderData);
                  setCart([]);
                  setShowCelebration(true);
                  setTimeout(() => setShowCelebration(false), 3000);
                }}
              />
            </div>
          </div>
        )}

        {/* Order Processing Overlay */}
        {currentOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-gray-900 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="text-center">
                <div className="text-6xl mb-4">🎉</div>
                <h2 className="text-2xl font-bold text-white mb-4">Unified Order Confirmed!</h2>
                <p className="text-gray-300 mb-6">
                  Order #{currentOrder.orderId} has been processed successfully!
                </p>
                
                {/* Order Summary */}
                <div className="bg-gray-800 rounded-lg p-4 mb-6 text-left">
                  <h3 className="text-lg font-bold text-yellow-400 mb-3">Order Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Total Amount:</span>
                      <span className="text-green-400 font-bold">${currentOrder.total?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Successful Vendors:</span>
                      <span className="text-green-400">{currentOrder.successfulVendors || 0}</span>
                    </div>
                    {currentOrder.failedVendors > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-300">Failed Vendors:</span>
                        <span className="text-red-400">{currentOrder.failedVendors}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-300">Payment Method:</span>
                      <span className="text-blue-400 capitalize">{currentOrder.paymentMethod}</span>
                    </div>
                  </div>
                </div>

                {/* Vendor Results */}
                {currentOrder.vendorOrders && (
                  <div className="bg-gray-800 rounded-lg p-4 mb-6 text-left">
                    <h3 className="text-lg font-bold text-yellow-400 mb-3">Vendor Orders</h3>
                    <div className="space-y-2">
                      {currentOrder.vendorOrders.map((vendor, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="text-gray-300">{vendor.vendorName}</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            vendor.success 
                              ? 'bg-green-600 text-white' 
                              : 'bg-red-600 text-white'
                          }`}>
                            {vendor.success ? '✓ Success' : '✗ Failed'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setCurrentOrder(null)}
                  className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-6 py-3 rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-all duration-300"
                >
                  Continue Exploring
                </button>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

export default App;